import type { FC, ReactNode } from 'react';
import { Button, Col, ConfigProvider, Divider, Row, Segmented, Space } from 'antd';
import { TbAdjustments, TbInfoCircle, TbLayersIntersect, TbMoon, TbPalette, TbSun } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import type { LogoDevBrand } from '@/queries';
import { PlatformAlert } from '@/shared/components/alerts';
import type { PlatformAlertStatus } from '@/shared/components/alerts';
import { SelectableOptionCard } from '@/shared/components/cards';
import { PlatformForm as Form, SettingsToggleRow } from '@/shared/components/forms';
import { EnrichedTooltip } from '@/shared/components/tooltips';
import { fontFamily } from '@/theme';
import type { WhitelabelSharedResourcesSettings, WhitelabelWorkspaceThemeSettings } from '@/types';

import { editableTokenKeys, palettePresets, ThemeMode, ThemeTokenKey, toTokenString } from '../../configuration';
import WhitelabelBrandImportCard from '../WhitelabelBrandImportCard';
import WhitelabelFieldLabel from '../WhitelabelFieldLabel';
import WhitelabelSectionCard from '../WhitelabelSectionCard';
import WhitelabelThemeTokenInput from '../WhitelabelThemeTokenInput';

interface WhitelabelWorkspaceTabProps {
    disabled?: boolean;
    isReuseLightThemeUpdated: boolean;
    isThemeFieldUpdated: (mode: ThemeMode, key: ThemeTokenKey) => boolean;
    isWebsiteUpdated: boolean;
    onApplyPalettePreset: (presetKey: string) => void;
    onBrandCleared: () => void;
    onBrandImported: (brand: NonNullable<LogoDevBrand>, website: string) => void;
    onReuseLightThemeChange: (checked: boolean) => void;
    onRevertReuseLightTheme: () => void;
    onRevertThemeField: (mode: ThemeMode, key: ThemeTokenKey) => void;
    onRevertWebsite: () => void;
    onThemeModeChange: (themeMode: ThemeMode) => void;
    onTokenChange: (key: ThemeTokenKey, value: string | number | undefined) => void;
    onWebsiteChange: (website: string) => void;
    sharedResources: WhitelabelSharedResourcesSettings;
    themeMode: ThemeMode;
    workspaceTheme: WhitelabelWorkspaceThemeSettings;
}

interface PreviewCanvasProps {
    backgroundColor?: string;
    children: ReactNode;
}

const PreviewCanvas: FC<PreviewCanvasProps> = ({ backgroundColor, children }): JSX.Element => {
    const { token } = useAntDesignTheme();

    return (
        <div
            style={{
                alignItems: 'center',
                backgroundColor: backgroundColor ?? token.colorBgContainer,
                border: `1px solid ${token.colorBorder}`,
                borderRadius: token.borderRadius,
                boxSizing: 'border-box',
                display: 'flex',
                justifyContent: 'center',
                minHeight: '5.5rem',
                minWidth: 0,
                padding: '0.75rem',
                width: '100%',
            }}
        >
            {children}
        </div>
    );
};

interface StatusPreviewProps {
    color: string;
    label: string;
    status: Exclude<PlatformAlertStatus, 'default'>;
}

const StatusPreview: FC<StatusPreviewProps> = ({ color, label, status }): JSX.Element => {
    const statusThemeToken =
        status === 'error'
            ? { colorError: color }
            : status === 'info'
              ? { colorInfo: color }
              : status === 'success'
                ? { colorSuccess: color }
                : { colorWarning: color };

    return (
        <PreviewCanvas>
            <ConfigProvider theme={{ token: statusThemeToken }}>
                <PlatformAlert showIcon size='small' status={status} title={label} />
            </ConfigProvider>
        </PreviewCanvas>
    );
};

interface ThemeTokenPreviewProps {
    field: (typeof editableTokenKeys)[number];
    value: string | number | undefined;
}

const ThemeTokenPreview: FC<ThemeTokenPreviewProps> = ({ field, value }): JSX.Element => {
    const { token } = useAntDesignTheme();
    const colorValue = toTokenString(value, token.colorPrimary);
    const radiusValue = typeof value === 'number' ? value : token.borderRadius;

    switch (field.key) {
        case 'colorPrimary':
            return (
                <PreviewCanvas>
                    <Button
                        tabIndex={-1}
                        type='primary'
                        style={{ backgroundColor: colorValue, borderColor: colorValue }}
                    >
                        Primary action
                    </Button>
                </PreviewCanvas>
            );
        case 'colorInfo':
            return <StatusPreview color={colorValue} label='Informational state' status='info' />;
        case 'colorSuccess':
            return <StatusPreview color={colorValue} label='Action completed' status='success' />;
        case 'colorWarning':
            return <StatusPreview color={colorValue} label='Attention required' status='warning' />;
        case 'colorError':
            return <StatusPreview color={colorValue} label='Validation failed' status='error' />;
        case 'colorBgBase':
            return (
                <PreviewCanvas backgroundColor={colorValue}>
                    <div
                        style={{
                            backgroundColor: token.colorBgContainer,
                            border: `1px solid ${token.colorBorder}`,
                            borderRadius: token.borderRadius,
                            height: '3rem',
                            width: '75%',
                        }}
                    />
                </PreviewCanvas>
            );
        case 'colorBgContainer':
            return (
                <PreviewCanvas backgroundColor={token.colorBgBase}>
                    <div
                        style={{
                            backgroundColor: colorValue,
                            border: `1px solid ${token.colorBorder}`,
                            borderRadius: token.borderRadius,
                            padding: '0.75rem',
                            width: '80%',
                        }}
                    >
                        <strong style={{ color: token.colorText }}>Container</strong>
                    </div>
                </PreviewCanvas>
            );
        case 'colorBgElevated':
            return (
                <PreviewCanvas backgroundColor={token.colorBgBase}>
                    <div
                        style={{
                            backgroundColor: colorValue,
                            border: `1px solid ${token.colorBorder}`,
                            borderRadius: token.borderRadius,
                            boxShadow: token.boxShadowSecondary,
                            padding: '0.75rem',
                            transform: 'translate(0.5rem, -0.25rem)',
                            width: '75%',
                        }}
                    >
                        <span style={{ color: token.colorText }}>Elevated panel</span>
                    </div>
                </PreviewCanvas>
            );
        case 'colorTextBase':
            return (
                <PreviewCanvas>
                    <div style={{ width: '100%' }}>
                        <strong style={{ color: colorValue, display: 'block' }}>Workspace heading</strong>
                        <span style={{ color: colorValue }}>Readable supporting text</span>
                    </div>
                </PreviewCanvas>
            );
        case 'colorBorder':
            return (
                <PreviewCanvas>
                    <div
                        style={{
                            border: `1px solid ${colorValue}`,
                            borderRadius: token.borderRadius,
                            color: token.colorTextTertiary,
                            padding: '0.5rem 0.75rem',
                            width: '100%',
                        }}
                    >
                        Bordered control
                    </div>
                </PreviewCanvas>
            );
        case 'colorBorderSecondary':
            return (
                <PreviewCanvas>
                    <div style={{ display: 'grid', gap: '0.375rem', width: '100%' }}>
                        {[0, 1].map((item) => (
                            <div
                                key={item}
                                style={{
                                    border: `1px solid ${colorValue}`,
                                    borderRadius: token.borderRadius,
                                    height: '1.5rem',
                                }}
                            />
                        ))}
                    </div>
                </PreviewCanvas>
            );
        case 'colorSplit':
            return (
                <PreviewCanvas>
                    <div style={{ width: '100%' }}>
                        <span style={{ color: token.colorText }}>Related content</span>
                        <Divider style={{ borderColor: colorValue, margin: '0.75rem 0' }} />
                        <span style={{ color: token.colorTextTertiary }}>Separated content</span>
                    </div>
                </PreviewCanvas>
            );
        case 'fontFamily':
            return (
                <PreviewCanvas>
                    <div
                        style={{
                            color: token.colorText,
                            fontFamily: toTokenString(value, fontFamily),
                            width: '100%',
                        }}
                    >
                        <strong style={{ display: 'block', fontSize: token.fontSizeLG }}>Workspace typography</strong>
                        <span style={{ color: token.colorTextTertiary }}>Aa Bb Cc 123</span>
                    </div>
                </PreviewCanvas>
            );
        case 'borderRadius':
            return (
                <PreviewCanvas>
                    <div
                        style={{
                            backgroundColor: token.colorPrimary,
                            borderRadius: radiusValue,
                            color: token.colorTextLightSolid,
                            padding: '0.5rem 1rem',
                        }}
                    >
                        Rounded control
                    </div>
                </PreviewCanvas>
            );
        default:
            return <PreviewCanvas>{field.label}</PreviewCanvas>;
    }
};

const WhitelabelWorkspaceTab: FC<WhitelabelWorkspaceTabProps> = ({
    disabled = false,
    isReuseLightThemeUpdated,
    isThemeFieldUpdated,
    isWebsiteUpdated,
    onApplyPalettePreset,
    onBrandCleared,
    onBrandImported,
    onReuseLightThemeChange,
    onRevertReuseLightTheme,
    onRevertThemeField,
    onRevertWebsite,
    onThemeModeChange,
    onTokenChange,
    onWebsiteChange,
    sharedResources,
    themeMode,
    workspaceTheme,
}): JSX.Element => {
    const { token } = useAntDesignTheme();
    const activeThemeTokens = workspaceTheme[themeMode];
    const iconStyle = { color: token.colorTextTertiary, verticalAlign: '-0.15rem' } as const;

    const enrichedLabel = (
        label: string,
        description: ReactNode,
        visual: ReactNode,
        learnMoreTopic: string = 'workspace appearance',
        updated = false,
        onRevert?: () => void,
    ) => (
        <WhitelabelFieldLabel label={label} onRevert={onRevert} updated={updated}>
            {label}
            <EnrichedTooltip description={description} label={label} learnMoreTopic={learnMoreTopic} visual={visual}>
                <TbInfoCircle
                    aria-hidden='true'
                    size='0.875rem'
                    style={{ color: token.colorTextTertiary, marginLeft: '0.25rem' }}
                />
            </EnrichedTooltip>
        </WhitelabelFieldLabel>
    );

    return (
        <Space direction='vertical' size='middle' style={{ width: '100%' }}>
            <WhitelabelBrandImportCard
                disabled={disabled}
                updated={isWebsiteUpdated}
                website={sharedResources.footer.website ?? ''}
                onChange={onWebsiteChange}
                onClear={onBrandCleared}
                onImported={onBrandImported}
                onRevert={onRevertWebsite}
            />

            <WhitelabelSectionCard
                description='Choose a professionally designed starting point, then tailor individual colors below.'
                icon={<TbPalette size='1rem' style={iconStyle} />}
                title='Palette Presets'
            >
                <div aria-label='Workspace palette presets' role='radiogroup'>
                    <Row gutter={[12, 12]}>
                        {palettePresets.map((preset) => (
                            <Col key={preset.key} xs={24} md={12}>
                                <SelectableOptionCard
                                    description={preset.description}
                                    icon={
                                        <span style={{ display: 'flex' }}>
                                            <span
                                                style={{
                                                    backgroundColor: toTokenString(preset.light.colorPrimary),
                                                    border: `2px solid ${token.colorBgContainer}`,
                                                    borderRadius: token.borderRadiusSM,
                                                    height: '1.5rem',
                                                    width: '1.5rem',
                                                }}
                                            />
                                            <span
                                                style={{
                                                    backgroundColor: toTokenString(preset.dark.colorPrimary),
                                                    border: `2px solid ${token.colorBgContainer}`,
                                                    borderRadius: token.borderRadiusSM,
                                                    height: '1.5rem',
                                                    marginLeft: '-0.5rem',
                                                    width: '1.5rem',
                                                }}
                                            />
                                        </span>
                                    }
                                    label={preset.label}
                                    selected={workspaceTheme.preset === preset.key}
                                    onSelect={() => onApplyPalettePreset(preset.key)}
                                />
                            </Col>
                        ))}
                    </Row>
                </div>
            </WhitelabelSectionCard>

            <WhitelabelSectionCard
                description='Use one consistent appearance or maintain distinct light and dark experiences.'
                icon={<TbSun size='1rem' style={iconStyle} />}
                title='Theme Modes'
            >
                <Row align='bottom' gutter={[12, 12]}>
                    <Col span={24}>
                        <Form.Item
                            label={enrichedLabel(
                                'Theme Mode',
                                'Choose which theme variant you are editing. Each mode can maintain its own accessible palette and surfaces.',
                                <div
                                    style={{
                                        display: 'grid',
                                        gap: '0.5rem',
                                        gridTemplateColumns: '1fr 1fr',
                                        minWidth: 0,
                                        width: '100%',
                                    }}
                                >
                                    <PreviewCanvas backgroundColor='#ffffff'>
                                        <TbSun color='#1f1f1f' size='1.25rem' />
                                    </PreviewCanvas>
                                    <PreviewCanvas backgroundColor='#141414'>
                                        <TbMoon color='#dcdcdc' size='1.25rem' />
                                    </PreviewCanvas>
                                </div>,
                                'light and dark workspace themes',
                            )}
                            style={{ marginBottom: 0 }}
                        >
                            <Segmented
                                block
                                disabled={workspaceTheme.reuseLightThemeForDark}
                                options={[
                                    {
                                        value: 'light',
                                        icon: <TbSun size='1rem' style={{ verticalAlign: '-0.175rem' }} />,
                                        label: 'Light',
                                    },
                                    {
                                        value: 'dark',
                                        icon: <TbMoon size='1rem' style={{ verticalAlign: '-0.175rem' }} />,
                                        label: 'Dark',
                                    },
                                ]}
                                value={themeMode}
                                onChange={(value) => onThemeModeChange(value as ThemeMode)}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <SettingsToggleRow
                            checked={workspaceTheme.reuseLightThemeForDark}
                            description='Use the light palette for both appearances instead of maintaining a separate dark palette.'
                            disabled={disabled}
                            label={enrichedLabel(
                                'Reuse Light Theme for Dark Mode',
                                'Reuse the light palette in dark mode when brand consistency matters more than separate mode-specific values.',
                                <PreviewCanvas>
                                    <div style={{ alignItems: 'center', display: 'flex', gap: '0.75rem' }}>
                                        <TbSun color={token.colorPrimary} size='1.25rem' />
                                        <TbLayersIntersect color={token.colorTextTertiary} size='1.25rem' />
                                        <TbMoon color={token.colorPrimary} size='1.25rem' />
                                    </div>
                                </PreviewCanvas>,
                                'dark mode theme behavior',
                                isReuseLightThemeUpdated,
                                onRevertReuseLightTheme,
                            )}
                            switchAriaLabel='Reuse light theme values for dark mode'
                            onChange={onReuseLightThemeChange}
                        />
                    </Col>
                </Row>
            </WhitelabelSectionCard>

            <WhitelabelSectionCard
                description='Fine-tune the colors, typography, and corner style applied throughout the workspace.'
                icon={<TbAdjustments size='1rem' style={iconStyle} />}
                title='Theme Colors'
            >
                <Row gutter={[12, 12]}>
                    {editableTokenKeys.map((field) => (
                        <Col key={field.key} xs={24} md={12}>
                            <Form.Item
                                label={enrichedLabel(
                                    field.label,
                                    field.description,
                                    <ThemeTokenPreview field={field} value={activeThemeTokens[field.key]} />,
                                    undefined,
                                    isThemeFieldUpdated(themeMode, field.key),
                                    () => onRevertThemeField(themeMode, field.key),
                                )}
                                style={{ marginBottom: 0 }}
                            >
                                <WhitelabelThemeTokenInput
                                    field={field}
                                    value={activeThemeTokens[field.key]}
                                    onChange={onTokenChange}
                                />
                            </Form.Item>
                        </Col>
                    ))}
                </Row>
            </WhitelabelSectionCard>
        </Space>
    );
};

export default WhitelabelWorkspaceTab;
